/**
 * Browser-facing integrity helpers (Wall 2 client cabinets + Wall 4/5 status).
 * Separate from messaging audit_logs. Swap-ready for a real integrity API.
 */

import { listAccessibleTransactions } from "../api/messaging/access";
import { writeAudit } from "../db/engine";

const CLIENT_CABINET_KEY = "dh_integrity_client_cabinets";
const INTEGRITY_EVENT_KEY = "dh_integrity_events";

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function appendBrowserIntegrityEvent(event) {
  const rows = readJson(INTEGRITY_EVENT_KEY, []);
  const row = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    ...event,
  };
  rows.push(row);
  writeJson(INTEGRITY_EVENT_KEY, rows.slice(-500));
  return row;
}

/** Wall 5 — lightweight freeze signal from open transactions on this device. */
export function browserFreezeSignals(session) {
  const txs = session ? listAccessibleTransactions(session) : [];
  const openPackets = txs.filter((t) => t.status === "open").length;
  return {
    openPackets,
    routingActive: openPackets > 0,
    peakHours: false,
  };
}

/**
 * Wall 2 — Client-side cabinet snapshot for the signed-in professional/client.
 * Downloads a JSON archive and records an integrity + messaging audit event.
 */
export function createBrowserClientCabinet(session, { note = "" } = {}) {
  const txs = session ? listAccessibleTransactions(session) : [];
  const cabinet = {
    side: "client",
    id: `client-browser-${Date.now()}`,
    createdAt: new Date().toISOString(),
    note,
    product: "rechecklist.pro",
    classes: {
      transaction_packet_export: txs.map((t) => ({
        transactionId: t.id,
        status: t.status,
        property_address: t.property_address || "",
      })),
      local_sync_mirror: { mirroredAt: new Date().toISOString() },
      compliance_archive: { count: txs.filter((t) => t.status !== "open").length },
      legal_packet_archive: { ready: true },
      folder_cabinet_snapshot: { transactionCount: txs.length },
    },
    restorePolicy: "If either Client or Vendor side glitches, the other side restores.",
  };

  const cabinets = readJson(CLIENT_CABINET_KEY, []);
  cabinets.push({ id: cabinet.id, createdAt: cabinet.createdAt, note });
  writeJson(CLIENT_CABINET_KEY, cabinets.slice(-50));

  appendBrowserIntegrityEvent({
    action_type: "backup.client.browser",
    wall: 2,
    metadata: { id: cabinet.id, transactionCount: txs.length },
  });

  if (session?.userId) {
    writeAudit({
      user_id: session.userId,
      action_type: "integrity.client_cabinet",
      transaction_id: null,
      metadata: { cabinetId: cabinet.id, transactionCount: txs.length },
    });
  }

  const blob = new Blob([JSON.stringify(cabinet, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${cabinet.id}.json`;
  link.click();
  URL.revokeObjectURL(url);

  return cabinet;
}

export function listBrowserClientCabinets() {
  return readJson(CLIENT_CABINET_KEY, []);
}

export function listBrowserIntegrityEvents(limit = 40) {
  return readJson(INTEGRITY_EVENT_KEY, []).slice(-limit).reverse();
}

/** Static Seven Walls catalog for the Admin integrity desk. */
export const SEVEN_WALLS = [
  {
    wall: 1,
    name: "CI/CD Gatekeeper",
    summary: "Blocks deploy unless build, schema, routing, and endpoint hashes match across environments.",
  },
  {
    wall: 2,
    name: "Dual-side backups",
    summary: "Vendor (primary/secondary/cold/immutable) and Client cabinets; either side restores the other.",
  },
  {
    wall: 3,
    name: "Deployment comps",
    summary: "Full diffs: file tree, schema, routing, transaction universe, packets, permissions, env.",
  },
  {
    wall: 4,
    name: "Immutable audit trails",
    summary: "Append-only integrity ledger for deploys, comps, backups, freezes, and packet custody.",
  },
  {
    wall: 5,
    name: "Deployment freeze windows",
    summary: "Scheduled windows plus auto-freeze on open packets, routing, and escrow spikes.",
  },
  {
    wall: 6,
    name: "Staging mirrors production",
    summary: "Exact match required — not close, not almost. Any drift blocks deploy.",
  },
  {
    wall: 7,
    name: "Deployment Guardian",
    summary: "Orchestrates every wall. Any failure → deployment blocked.",
  },
];
